import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Users, Plus, Trash2, Crown, Mail, Phone, Search, X, Pencil } from "lucide-react";
import { formatPhoneNumber } from "@/lib/utils";
import EditContactModal from "@/components/modals/edit-contact-modal";
import { insertContactSchema } from "@shared/schema";
import { z } from "zod";

interface CompanyContactsProps {
  companyId: number;
  companyName: string;
}

interface CompanyContact {
  id: number;
  contactId: number;
  companyId: number;
  relationshipType: string;
  isPrimary: boolean;
  notes?: string;
  isActive: boolean;
  contact: {
    id: number;
    firstName: string;
    lastName: string;
    email?: string;
    phone?: string;
    jobTitle?: string;
    department?: string;
  };
}

const relationshipTypes = [
  { value: "owner", label: "Owner" },
  { value: "billing", label: "Billing" },
  { value: "operations", label: "Operations" },
  { value: "safety", label: "Safety" },
  { value: "dispatch", label: "Dispatch" },
  { value: "compliance", label: "Compliance" },
  { value: "emergency", label: "Emergency" },
  { value: "other", label: "Other" },
];

export default function CompanyContactsSection({ companyId, companyName }: CompanyContactsProps) {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState<string>("");
  const [selectedRelationshipType, setSelectedRelationshipType] = useState<string>("other");
  const [isPrimary, setIsPrimary] = useState(false);
  const [notes, setNotes] = useState("");
  const [contactSearch, setContactSearch] = useState("");
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const [selectedContact, setSelectedContact] = useState<any>(null);
  const [mode, setMode] = useState<"search" | "create">("search");
  const [newFirstName, setNewFirstName] = useState("");
  const [newLastName, setNewLastName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newJobTitle, setNewJobTitle] = useState("");
  const [editingContact, setEditingContact] = useState<any>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: companyContacts = [], isLoading } = useQuery<CompanyContact[]>({
    queryKey: [`/api/companies/${companyId}/contacts`],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/companies/${companyId}/contacts`);
      return response.json();
    },
  });

  const { data: allContacts = [] } = useQuery({
    queryKey: ["/api/contacts"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/contacts");
      return response.json();
    },
  });

  const addContactMutation = useMutation({
    mutationFn: async (data: {
      companyId: number;
      relationshipType: string;
      isPrimary: boolean;
      notes?: string;
    }) => {
      await apiRequest("POST", `/api/contacts/${selectedContactId}/companies`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/companies/${companyId}/contacts`] });
      queryClient.invalidateQueries({ queryKey: ["/api/contacts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/companies", companyId] });
      setIsAddModalOpen(false);
      resetForm();
      toast({
        title: "Success",
        description: "Contact relationship added successfully",
      });
    },
    onError: (error) => {
      console.error("Error adding contact relationship:", error);
      toast({
        title: "Error",
        description: "Failed to add contact relationship",
        variant: "destructive",
      });
    },
  });

  const createAndLinkContactMutation = useMutation({
    mutationFn: async (data: {
      firstName: string;
      lastName: string;
      email?: string;
      phone?: string;
      jobTitle?: string;
      companyId: number;
      relationshipType: string;
      isPrimary: boolean;
      notes?: string;
    }) => {
      const contactResponse = await apiRequest("POST", "/api/contacts", {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email || null,
        phone: data.phone || null,
        jobTitle: data.jobTitle || null,
      });
      
      if (!contactResponse.ok) {
        const errorData = await contactResponse.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to create contact");
      }
      
      const newContact = await contactResponse.json();
      
      if (!newContact?.id) {
        throw new Error("Invalid response from contact creation");
      }
      
      const linkResponse = await apiRequest("POST", `/api/contacts/${newContact.id}/companies`, {
        companyId: data.companyId,
        relationshipType: data.relationshipType,
        isPrimary: data.isPrimary,
        notes: data.notes,
      });
      
      if (!linkResponse.ok) {
        const errorData = await linkResponse.json().catch(() => ({}));
        throw new Error(errorData.message || "Contact created but failed to link to company");
      }
      
      return newContact;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/companies/${companyId}/contacts`] });
      queryClient.invalidateQueries({ queryKey: ["/api/contacts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/companies", companyId] });
      setIsAddModalOpen(false);
      resetForm();
      toast({
        title: "Success",
        description: "Contact created and linked successfully",
      });
    },
    onError: (error: Error) => {
      console.error("Error creating contact:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to create contact",
        variant: "destructive",
      });
    },
  });

  const removeContactMutation = useMutation({
    mutationFn: async (contactId: number) => {
      await apiRequest("DELETE", `/api/contacts/${contactId}/companies/${companyId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/companies/${companyId}/contacts`] });
      toast({
        title: "Success",
        description: "Contact relationship removed successfully",
      });
    },
    onError: (error) => {
      console.error("Error removing contact relationship:", error);
      toast({
        title: "Error",
        description: "Failed to remove contact relationship",
        variant: "destructive",
      });
    },
  });

  const updatePrimaryMutation = useMutation({
    mutationFn: async (contactId: number) => {
      await apiRequest("PUT", `/api/contacts/${contactId}/companies/${companyId}`, {
        isPrimary: true,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/companies/${companyId}/contacts`] });
      toast({
        title: "Success",
        description: "Primary contact updated successfully",
      });
    },
    onError: (error) => {
      console.error("Error updating primary contact:", error);
      toast({
        title: "Error",
        description: "Failed to update primary contact",
        variant: "destructive",
      });
    },
  });

  const updateContactMutation = useMutation({
    mutationFn: async (contactData: z.infer<typeof insertContactSchema>) => {
      await apiRequest("PUT", `/api/contacts/${editingContact?.id}`, contactData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/companies/${companyId}/contacts`] });
      queryClient.invalidateQueries({ queryKey: ["/api/contacts"] });
      queryClient.invalidateQueries({ queryKey: ['/api/contacts', 'with-companies'] });
      if (editingContact?.id) {
        queryClient.invalidateQueries({ queryKey: ['/api/contacts', editingContact.id] });
      }
      setIsEditModalOpen(false);
      setEditingContact(null);
      toast({
        title: "Success",
        description: "Contact updated successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update contact",
        variant: "destructive",
      });
    },
  });

  const handleEditContact = async (contact: CompanyContact["contact"]) => {
    try {
      const response = await apiRequest("GET", `/api/contacts/${contact.id}`);
      const fullContact = await response.json();
      setEditingContact(fullContact);
      setIsEditModalOpen(true);
    } catch {
      setEditingContact(contact);
      setIsEditModalOpen(true);
    }
  };

  const handleUpdateContact = (contactData: z.infer<typeof insertContactSchema>) => {
    updateContactMutation.mutate(contactData);
  };

  const resetForm = () => {
    setSelectedContactId("");
    setSelectedContact(null);
    setSelectedRelationshipType("other");
    setIsPrimary(false);
    setNotes("");
    setContactSearch("");
    setShowContactDropdown(false);
    setMode("search");
    setNewFirstName("");
    setNewLastName("");
    setNewEmail("");
    setNewPhone("");
    setNewJobTitle("");
  };

  const handleAddContact = () => {
    if (!selectedContactId || !selectedRelationshipType) {
      toast({
        title: "Error",
        description: "Please select a contact and relationship type",
        variant: "destructive",
      });
      return;
    }

    addContactMutation.mutate({
      companyId,
      relationshipType: selectedRelationshipType,
      isPrimary,
      notes: notes || undefined,
    });
  };

  const handleCreateAndLinkContact = () => {
    if (!newFirstName.trim() || !newLastName.trim()) {
      toast({
        title: "Error",
        description: "First name and last name are required",
        variant: "destructive",
      });
      return;
    }

    createAndLinkContactMutation.mutate({
      firstName: newFirstName.trim(),
      lastName: newLastName.trim(),
      email: newEmail.trim() || undefined,
      phone: newPhone.trim() || undefined,
      jobTitle: newJobTitle.trim() || undefined,
      companyId,
      relationshipType: selectedRelationshipType,
      isPrimary,
      notes: notes || undefined,
    });
  };

  const handleContactSelect = (contact: any) => {
    setSelectedContact(contact);
    setSelectedContactId(contact.id.toString());
    setContactSearch("");
    setShowContactDropdown(false);
  };

  const clearSelectedContact = () => {
    setSelectedContact(null);
    setSelectedContactId("");
    setContactSearch("");
  };

  const handleRemoveContact = (contactId: number, contactName: string) => {
    if (confirm(`Are you sure you want to remove ${contactName} from ${companyName}?`)) {
      removeContactMutation.mutate(contactId);
    }
  };

  const handleSetPrimary = (contactId: number) => {
    updatePrimaryMutation.mutate(contactId);
  };

  const getRelationshipTypeLabel = (type: string) => {
    return relationshipTypes.find(rt => rt.value === type)?.label || type;
  };

  const getBadgeVariant = (type: string) => {
    switch (type) {
      case "owner": return "default";
      case "billing": return "secondary";
      case "operations": return "outline";
      default: return "outline";
    }
  };

  // Get contacts that aren't already linked to this company
  const availableContacts = allContacts.filter((contact: any) => 
    !companyContacts.some(cc => cc.contact.id === contact.id)
  );

  // Fuzzy search for contacts
  const filteredContacts = useMemo(() => {
    if (!contactSearch.trim()) return availableContacts;
    
    const searchTerm = contactSearch.toLowerCase();
    return availableContacts.filter((contact: any) =>
      contact.firstName?.toLowerCase().includes(searchTerm) ||
      contact.lastName?.toLowerCase().includes(searchTerm) ||
      contact.email?.toLowerCase().includes(searchTerm) ||
      contact.phone?.toLowerCase().includes(searchTerm) ||
      contact.jobTitle?.toLowerCase().includes(searchTerm) ||
      contact.department?.toLowerCase().includes(searchTerm)
    );
  }, [availableContacts, contactSearch]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Company Contacts
          </CardTitle>
          
          <Dialog open={isAddModalOpen} onOpenChange={(open) => { setIsAddModalOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Add Contact
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Add Contact to {companyName}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex border rounded-lg overflow-hidden">
                  <button
                    type="button"
                    className={`flex-1 py-2 px-4 text-sm font-medium transition-colors ${
                      mode === "search" 
                        ? "bg-primary text-primary-foreground" 
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                    onClick={() => setMode("search")}
                  >
                    <Search className="w-4 h-4 inline mr-2" />
                    Search Existing
                  </button>
                  <button
                    type="button"
                    className={`flex-1 py-2 px-4 text-sm font-medium transition-colors ${
                      mode === "create" 
                        ? "bg-primary text-primary-foreground" 
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                    onClick={() => setMode("create")}
                  >
                    <Plus className="w-4 h-4 inline mr-2" />
                    Create New
                  </button>
                </div>

                {mode === "search" ? (
                  <div>
                    <Label htmlFor="contact">Search Contact</Label>
                    {selectedContact ? (
                      <div className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
                        <div className="flex-1">
                          <div className="font-medium">
                            {selectedContact.firstName} {selectedContact.lastName}
                          </div>
                          <div className="text-sm text-gray-600">
                            {selectedContact.email && <span>{selectedContact.email}</span>}
                            {selectedContact.email && selectedContact.phone && <span> • </span>}
                            {selectedContact.phone && <span>{formatPhoneNumber(selectedContact.phone)}</span>}
                          </div>
                          {selectedContact.jobTitle && (
                            <div className="text-sm text-gray-500">{selectedContact.jobTitle}</div>
                          )}
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={clearSelectedContact}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="relative">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
                          <Input
                            placeholder="Search contacts by name, email, phone..."
                            value={contactSearch}
                            onChange={(e) => {
                              setContactSearch(e.target.value);
                              setShowContactDropdown(true);
                            }}
                            onFocus={() => setShowContactDropdown(true)}
                            className="pl-10"
                          />
                        </div>
                        
                        {showContactDropdown && contactSearch && filteredContacts.length > 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-64 overflow-y-auto">
                            {filteredContacts.slice(0, 15).map((contact: any) => (
                              <div
                                key={contact.id}
                                className="px-3 py-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                                onClick={() => handleContactSelect(contact)}
                              >
                                <div className="font-medium">
                                  {contact.firstName} {contact.lastName}
                                </div>
                                <div className="text-sm text-gray-600">
                                  {contact.email && <span>{contact.email}</span>}
                                  {contact.email && contact.phone && <span> • </span>}
                                  {contact.phone && <span>{formatPhoneNumber(contact.phone)}</span>}
                                </div>
                                {contact.jobTitle && (
                                  <div className="text-sm text-gray-500">{contact.jobTitle}</div>
                                )}
                                {contact.department && (
                                  <div className="text-xs text-gray-400">{contact.department}</div>
                                )}
                              </div>
                            ))}
                            {filteredContacts.length > 15 && (
                              <div className="px-3 py-2 text-sm text-gray-500 bg-gray-50">
                                Showing first 15 results. Continue typing to refine search.
                              </div>
                            )}
                          </div>
                        )}
                        
                        {showContactDropdown && contactSearch && filteredContacts.length === 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg">
                            <div className="px-3 py-3 text-sm text-gray-500 text-center">
                              No contacts found matching "{contactSearch}"
                              <Button
                                variant="link"
                                size="sm"
                                className="block mx-auto mt-1"
                                onClick={() => setMode("create")}
                              >
                                Create new contact instead
                              </Button>
                            </div>
                          </div>
                        )}

                        {availableContacts.length === 0 && !contactSearch && (
                          <div className="mt-2 p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
                            All existing contacts are already linked to this company.
                            <Button
                              variant="link"
                              size="sm"
                              className="block mt-1 p-0"
                              onClick={() => setMode("create")}
                            >
                              Create a new contact
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="firstName">First Name *</Label>
                        <Input
                          id="firstName"
                          value={newFirstName}
                          onChange={(e) => setNewFirstName(e.target.value)}
                          placeholder="John"
                        />
                      </div>
                      <div>
                        <Label htmlFor="lastName">Last Name *</Label>
                        <Input
                          id="lastName"
                          value={newLastName}
                          onChange={(e) => setNewLastName(e.target.value)}
                          placeholder="Doe"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        placeholder="john@example.com"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        value={newPhone}
                        onChange={(e) => setNewPhone(e.target.value)}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    <div>
                      <Label htmlFor="jobTitle">Job Title</Label>
                      <Input
                        id="jobTitle"
                        value={newJobTitle}
                        onChange={(e) => setNewJobTitle(e.target.value)}
                        placeholder="Operations Manager"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <Label htmlFor="relationshipType">Relationship Type</Label>
                  <Select value={selectedRelationshipType} onValueChange={setSelectedRelationshipType}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select relationship type" />
                    </SelectTrigger>
                    <SelectContent>
                      {relationshipTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="isPrimary"
                    checked={isPrimary}
                    onCheckedChange={(checked) => setIsPrimary(checked === true)}
                  />
                  <Label htmlFor="isPrimary">Primary Contact</Label>
                </div>

                <div>
                  <Label htmlFor="notes">Notes (optional)</Label>
                  <Textarea
                    id="notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Additional notes about this relationship..."
                    rows={2}
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setIsAddModalOpen(false)}>
                    Cancel
                  </Button>
                  {mode === "search" ? (
                    <Button 
                      onClick={handleAddContact} 
                      disabled={addContactMutation.isPending || !selectedContactId}
                    >
                      {addContactMutation.isPending ? "Adding..." : "Add Contact"}
                    </Button>
                  ) : (
                    <Button 
                      onClick={handleCreateAndLinkContact} 
                      disabled={createAndLinkContactMutation.isPending || !newFirstName.trim() || !newLastName.trim()}
                    >
                      {createAndLinkContactMutation.isPending ? "Creating..." : "Create & Add Contact"}
                    </Button>
                  )}
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-4 text-gray-500">Loading contacts...</div>
        ) : companyContacts.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <h3 className="font-medium text-gray-900 mb-2">No Contacts</h3>
            <p className="text-sm">
              No contacts are currently associated with {companyName}.
            </p>
            {availableContacts.length > 0 && (
              <p className="text-sm mt-2">Click "Add Contact" to create a relationship.</p>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {companyContacts.map((relationship) => (
              <div
                key={relationship.id}
                className="flex items-center justify-between p-3 border rounded-lg bg-gray-50"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium text-gray-900">
                      {relationship.contact.firstName} {relationship.contact.lastName}
                    </h4>
                    {relationship.isPrimary && (
                      <span title="Primary Contact"><Crown className="w-4 h-4 text-yellow-500" /></span>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant={getBadgeVariant(relationship.relationshipType)}>
                      {getRelationshipTypeLabel(relationship.relationshipType)}
                    </Badge>
                    {relationship.contact.jobTitle && (
                      <Badge variant="outline">{relationship.contact.jobTitle}</Badge>
                    )}
                  </div>

                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    {relationship.contact.email && (
                      <div className="flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {relationship.contact.email}
                      </div>
                    )}
                    {relationship.contact.phone && (
                      <div className="flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {formatPhoneNumber(relationship.contact.phone)}
                      </div>
                    )}
                  </div>

                  {relationship.notes && (
                    <p className="text-sm text-gray-600 mt-1">{relationship.notes}</p>
                  )}
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEditContact(relationship.contact)}
                    title="Edit contact"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>

                  {!relationship.isPrimary && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleSetPrimary(relationship.contact.id)}
                      disabled={updatePrimaryMutation.isPending}
                      title="Set as primary contact"
                    >
                      <Crown className="w-4 h-4" />
                    </Button>
                  )}
                  
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Remove Contact Relationship</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to remove {relationship.contact.firstName} {relationship.contact.lastName} from {companyName}? This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleRemoveContact(relationship.contact.id, `${relationship.contact.firstName} ${relationship.contact.lastName}`)}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          Remove
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        )}

        {availableContacts.length === 0 && companyContacts.length > 0 && (
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              All available contacts are already linked to this company.
            </p>
          </div>
        )}
      </CardContent>

      {editingContact && (
        <EditContactModal
          open={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setEditingContact(null);
          }}
          onSubmit={handleUpdateContact}
          isLoading={updateContactMutation.isPending}
          contact={editingContact}
        />
      )}
    </Card>
  );
}